import { css } from "@emotion/react";
import type { Publication } from "@rowan/prisma";
import type { RowClickedEvent } from "ag-grid-community";
import { useState } from "react";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton } from "../../bits/buttons";
import { AdminTable } from "../../bits/inputs/tables/AdminTable";
import { publicationColDefs } from "../../bits/inputs/tables/publicationColDefs";
import { FlexRowContainer } from "../../bits/layout";
import { LoadingSpinner } from "../../bits/loading";
import { Modal } from "../../bits/Modal";
import { useTitle } from "../../hooks/useTitle";
import type { PublicationFormData } from "../../patterns/AddPublicationForm";
import { AddPublicationForm } from "../../patterns/AddPublicationForm";

export const PublicationAdmin = () => {
  useTitle("Publication Admin | Compass");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingPublication, setEditingPublication] = useState<Publication>();
  const createPublication = trpc.createPublication.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingPublication(undefined);
    },
  });
  const deletePublication = trpc.deletePublication.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setEditingPublication(undefined);
      setIsModalOpen(false);
    },
  });
  const updatePublication = trpc.adminUpdatePublication.useMutation({
    onSuccess: () => {
      setEditingPublication(undefined);
      setIsModalOpen(false);
    },
  });

  const [customError, setCustomError] = useState<string | null>(null);

  const { data } = trpc.adminGetPublications.useQuery(undefined, {
    // refetch every 10 seconds to check for publication updates
    refetchInterval: 10000,
  });
  const handleSubmit = (publication: PublicationFormData) => {
    setCustomError(null);
    if (editingPublication) {
      updatePublication
        .mutateAsync({
          id: editingPublication.id,
          ...publication,
          ...(publication.files?.length
            ? {
                filename: publication.files[0].name,
                fileType: publication.files[0].type,
                // Only set status to UPDATING if the publication is being created
                // otherwise leave it as is
                ...(editingPublication.status === "CREATING"
                  ? { status: "UPDATING" }
                  : {}),
              }
            : {}),
          ...(publication.images?.length
            ? {
                imageFilename: publication.images[0].name,
                imageFileType: publication.images[0].type,
              }
            : {}),
        })
        .then((res) => {
          // if filename is set and a signed url is returned
          // then upload the file to s3
          if (res.filename && res.signedUrl) {
            const file = publication.files?.[0];
            if (file) {
              fetch(res.signedUrl, {
                method: "PUT",
                body: file,
                headers: {
                  "Content-Type": file.type,
                },
              }).catch((err) => {
                console.error("Error uploading file", err);
                setCustomError("Error uploading file, please try again later");
              });
            }
          }
          if (res.imageSignedUrl) {
            const image = publication.images?.[0];
            if (image) {
              fetch(res.imageSignedUrl, {
                method: "PUT",
                body: image,
                headers: {
                  "Content-Type": image.type,
                },
              }).catch((err) => {
                console.error("Error uploading image", err);
                setCustomError("Error uploading image, please try again later");
              });
            }
          }
        });
    } else {
      createPublication.mutate(publication);
    }
  };

  const handleSelectRow = (event: RowClickedEvent) => {
    setEditingPublication(event.data);
    setIsModalOpen(true);
  };

  return (
    <div style={{ padding: "1rem" }}>
      <Modal
        key="modal"
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPublication(undefined);
          updatePublication.reset();
          createPublication.reset();
          deletePublication.reset();
        }}
        renderButton={(props) => (
          <FlexRowContainer style={{ justifyContent: "end" }}>
            <PrimaryButton
              {...props}
              onClick={() => setIsModalOpen(true)}
              disabled={createPublication.isLoading}
              style={{ marginBottom: "1rem" }}
            >
              Add new Publication
            </PrimaryButton>
          </FlexRowContainer>
        )}
        header={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "8px",
              width: "100%",
            }}
          >
            <h2>
              {editingPublication
                ? "Update publication"
                : "Add new publication"}
            </h2>
            {editingPublication && (
              <Modal
                key="deleteModal"
                isOpen={isDeleteModalOpen}
                onClose={() => {
                  setIsDeleteModalOpen(false);
                }}
                renderButton={() => (
                  <FlexRowContainer style={{ justifyContent: "end" }}>
                    <PrimaryButton
                      onClick={() => setIsDeleteModalOpen(true)}
                      disabled={createPublication.isLoading}
                    >
                      Delete Publication
                    </PrimaryButton>
                  </FlexRowContainer>
                )}
                header={<h2>Delete Publication</h2>}
                content={
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 8px;
                    `}
                  >
                    Are you sure you want to delete {editingPublication.title}?
                  </div>
                }
                footer={
                  <PrimaryButton
                    onClick={() => {
                      deletePublication.mutate(editingPublication);
                    }}
                  >
                    Delete
                  </PrimaryButton>
                }
              />
            )}
          </div>
        }
        content={
          <AddPublicationForm
            onSubmit={handleSubmit}
            error={
              createPublication.error?.message ??
              updatePublication.error?.message ??
              deletePublication.error?.message ??
              customError ??
              ""
            }
            publication={editingPublication}
          />
        }
        footer={<></>}
      />
      {data ? (
        <AdminTable
          rowData={data}
          columnDefs={publicationColDefs}
          onRowClicked={handleSelectRow}
        />
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};
